import { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import { isDesktop } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { AppTracker, BottomPanelKey, ChannelSchema, EventSchema, UserActionType } from 'types';
import {
  diffTimes,
  displayDateTime,
  getChannelURL,
  getDuration,
  SCHEDULED_EVENT_OVERDUE,
  UZBEKITTV_CHANNEL_NAME
} from 'utils';
import { profileActions } from 'store/actions';
import {
  authSelector,
  hasAvailableMinutesSelector,
  hasChannelSelector,
  isMemberSelector,
  isSubscribedSelector
} from 'store/selectors';
import { callbackTriggersActions } from 'store/slices/callbackTriggers';
import { api, LoggerService, tracker } from 'services';
import { useBottomSheet, useConfirmation, useRouter, useStore, useToast } from 'hooks';
import { BottomPanels, ConfirmPanel, EditScheduledEventPanel, Navbar, SetupPanel, UpgradePanel } from 'containers';
import {
  CalendarIcon,
  CameraIcon,
  CheckedIcon,
  DeleteIcon,
  EditIcon,
  LockClosedIcon,
  LockOpenedIcon,
  NoPhotoIcon,
  RecordingIcon,
  ShareIcon,
  TVIcon
} from 'components/Icon';

import Countdown from './Countdown';
import OverduePanel from './OverduePanel';
import SharePanel from './SharePanel';

import styles from './UpcomingEventDetail.module.scss';

export type EventDetailProps = {
  channel?: ChannelSchema;
  event: EventSchema;
  onStarted?(channelName: string, eventCode: string, options: { audioMuted: boolean; videoMuted: boolean }): void;
  onEdited?(updatedEvent: EventSchema): void;
  onClose?(): void;
  onBecomeMember?(): void;
};

const UpcomingEventDetail = ({
  channel,
  event,
  onStarted,
  onClose,
  onEdited,
  onBecomeMember
}: EventDetailProps): JSX.Element => {
  const { channelName, poster, hostUsername, startDate, code, name, memberOnly, recordingEnabled, description } = event;

  const toast = useToast();
  const router = useRouter();
  const dispatch = useDispatch();
  const confirmation = useConfirmation<'DELETE_EVENT'>();
  const bottomSheet = useBottomSheet<
    | BottomPanelKey.UPDATE_PLAN
    | BottomPanelKey.SETUP
    | BottomPanelKey.EDIT_SCHEDULED_EVENT
    | BottomPanelKey.SHARE
    | BottomPanelKey.EVENT_IS_OVERDUE
  >();
  const { auth, isSubscribed, isMember, hasAvailableMinutes, hasOwnChannel } = useStore(state => ({
    auth: authSelector(state),
    isSubscribed: isSubscribedSelector(event.channelName)(state),
    isMember: isMemberSelector(channelName)(state),
    hasAvailableMinutes: hasAvailableMinutesSelector(state),
    hasOwnChannel: hasChannelSelector(state)
  }));

  const [posterLoadFailed, setPosterLoadFailed] = useState(!poster);

  // TODO: Find another solution for uzbekittv channel custom buttons
  const isUzbekITTV = channelName.toLowerCase() === UZBEKITTV_CHANNEL_NAME;

  const isOwnEvent = hostUsername === auth.username;
  const overdue = new Date() > new Date(startDate);
  const startsSoon = getDuration(startDate) <= 1000 * 60 * 30;
  const isActive = diffTimes(startDate) > -1 * SCHEDULED_EVENT_OVERDUE;
  const hasMembershipPlan = !!channel?.membershipPricingPlan;
  const canSubscribeToChannel = !isOwnEvent && !!channel;

  useEffect(() => {
    tracker.openEventDetailPage(event.code);
  }, []);

  // reset failed state when poster is changed
  useEffect(() => {
    setPosterLoadFailed(!poster);
  }, [poster]);

  // show overdue panel initially if the event is overdue
  useEffect(() => {
    if (isOwnEvent && !isActive) {
      bottomSheet.open(BottomPanelKey.EVENT_IS_OVERDUE);
    }
  }, []);

  const handleOnOpenSharePanel = () => {
    bottomSheet.open(BottomPanelKey.SHARE);
  };

  const handleOnPosterLoadFailed = useCallback(() => setPosterLoadFailed(true), []);

  const handleOnStart = () => {
    const canStart = overdue || startsSoon;

    if (!canStart) {
      toast.info('Start video chat is enabled on last 30 minutes', { id: 'START_IS_NOT_AVAILABLE_YET' });
      return;
    }

    if (!hasAvailableMinutes) {
      bottomSheet.open(BottomPanelKey.UPDATE_PLAN);
      return;
    }

    bottomSheet.open(BottomPanelKey.SETUP, {
      params: { eventCode: code, eventName: name }
    });
  };

  const handleOnSubscribe = () => {
    if (!auth.isAuth) {
      dispatch(
        callbackTriggersActions.set([
          {
            type: UserActionType.REDIRECT,
            payload: { path: router.pathname }
          }
        ])
      );

      router.push('/login');
    } else {
      subscribe();
    }
  };

  const handleOnEdit = () => bottomSheet.open(BottomPanelKey.EDIT_SCHEDULED_EVENT);

  const handleOnEdited = (updatedEvent: EventSchema) => {
    bottomSheet.close();

    onEdited?.(updatedEvent);
  };

  const handleOnDelete = () => {
    confirmation.open('DELETE_EVENT');
  };

  const handleOnDeletionCancelled = () => {
    confirmation.close();
  };

  const handleOnDeletionSubmitted = async () => {
    try {
      await api.requests.ltv.deleteScheduledEvent(code);

      if (router.canGoBack) {
        router.goBack();
      } else {
        router.replace(getChannelURL(channelName));
      }

      confirmation.close();
    } catch (e) {
      LoggerService.log('DELETE_UPCOMING_EVENT', e);
      if (e instanceof Error) {
        toast.error(e, { id: 'DELETE_UPCOMING_EVENT' });
      }
    }
  };

  const renderSubscribe = () => {
    // TODO: Find another solution for uzbekittv channel custom buttons
    if (isUzbekITTV) {
      return null;
    }

    if (!canSubscribeToChannel) {
      return null;
    }

    if (isSubscribed) {
      return (
        <button type="button" disabled className={cx(styles.subscribed)}>
          <CheckedIcon className="mr-6" />
          <span>Subscribed</span>
        </button>
      );
    }

    return (
      <button
        type="button"
        className={cx(styles.subscribe, { [styles.single]: !memberOnly })}
        onClick={handleOnSubscribe}
      >
        Subscribe
      </button>
    );
  };

  const subscribe = async () => {
    if (!channel) return;

    try {
      tracker.subscribeToChannel(channel.id, channel.name, AppTracker.ChannelSubscriptionSource.EVENT_DETAIL_PAGE);
      dispatch(profileActions.addChannel(channel));

      await api.requests.ltv.subscribeToChannel(channel.id);
      tracker.subscribeToChannelSuccess(
        channel.id,
        channel.name,
        AppTracker.ChannelSubscriptionSource.EVENT_DETAIL_PAGE
      );

      api.requests.ltv
        .getSubscribedChannels()
        .then(response => {
          dispatch(profileActions.setChannels(response.data));
        })
        .catch(e => {
          LoggerService.log('GET_SUBSCRIBED_CHANNELS', e);
        });

      toast.success('Subscribed!', { id: `SUBSCRIBE_TO_CHANNEL:${channel.id}` });
    } catch (error) {
      LoggerService.log('SUBSCRIBE_TO_CHANNEL_ON_EVENT_DETAIL', error);

      if (error instanceof Error) {
        tracker.subscribeToChannelFail(
          channel.id,
          channel.name,
          AppTracker.ChannelSubscriptionSource.EVENT_DETAIL_PAGE,
          error
        );
      }
      dispatch(profileActions.removeChannel({ channelId: channel.id }));

      toast.error('Subscribe request is failed', { id: `SUBSCRIBE_TO_CHANNEL:${channel.id}` });
    }
  };

  const renderBecomeMember = () => {
    if (isOwnEvent) {
      return null;
    }

    if (isMember) {
      return (
        <button type="button" className={styles.becomeMember} disabled>
          <CheckedIcon className="mr-4" />
          <span>{isUzbekITTV ? 'Siz obunachisiz' : 'You’re a member'}</span>
        </button>
      );
    }

    if (hasMembershipPlan) {
      return (
        <button type="button" className={styles.becomeMember} onClick={onBecomeMember}>
          {/* // TODO: Find another solution for uzbekittv channel custom buttons */}
          {isUzbekITTV ? "Obuna bo'lish" : 'Become a member'}
        </button>
      );
    }

    return null;
  };

  const renderPoster = () =>
    posterLoadFailed || !poster ? (
      <div className={styles.placeholder}>
        <NoPhotoIcon />
        <span>No cover photo</span>
      </div>
    ) : (
      <>
        <img className={styles.cover} src={poster} alt="poster" />
        <img className={styles.image} src={poster} alt="poster" onError={handleOnPosterLoadFailed} />
      </>
    );

  return (
    <>
      <Navbar
        className={styles.header}
        title="Upcoming event"
        right={
          <button type="button" className={styles.share} onClick={handleOnOpenSharePanel}>
            <ShareIcon width={24} height={24} />
          </button>
        }
      />

      <div className={styles.container}>
        <div className={styles.poster}>{renderPoster()}</div>

        <div className={styles.content}>
          {(overdue || startsSoon) && <Countdown startDate={startDate} />}

          <h3 className={styles.title}>{name}</h3>
          {description && <p className={styles.description}>{description}</p>}

          <ul className={styles.extra}>
            <li>
              <div className={styles.item}>
                <CalendarIcon className="mr-4" width={18} height={18} color="#4FC940" />
                <span>{displayDateTime(startDate)}</span>
              </div>
            </li>
            <li>
              <Link to={`/${event.channelName}`} onClick={onClose}>
                <div className={styles.item}>
                  <TVIcon className="mr-4" width={18} height={18} color="#4FC940" />
                  <span>@{event.channelName}</span>
                </div>
              </Link>
            </li>
            {recordingEnabled && (
              <li>
                <div className={styles.item}>
                  <RecordingIcon className="mr-4" width={18} height={18} color="#4FC940" />
                  <span>Recorded</span>
                </div>
              </li>
            )}
            {memberOnly && (
              <li>
                <div className={styles.item}>
                  {isMember ? (
                    <LockOpenedIcon className="mr-4" width={18} height={18} color="#4FC940" />
                  ) : (
                    <LockClosedIcon className="mr-4" width={18} height={18} color="#FE514E" />
                  )}
                  <span>Members Only</span>
                </div>
              </li>
            )}
          </ul>

          {canSubscribeToChannel && (
            <div className={cx(styles.actions, styles.viewer)}>
              {renderSubscribe()}
              {memberOnly && renderBecomeMember()}
            </div>
          )}

          {isOwnEvent && isActive && (
            <div className={cx(styles.actions, styles.host)}>
              <button type="button" className={styles.edit} onClick={handleOnEdit}>
                <EditIcon />
                <span>Edit details</span>
              </button>
              <button type="button" className={styles.delete} onClick={handleOnDelete}>
                <DeleteIcon />
                <span>Delete event</span>
              </button>
              <button type="button" className={styles.start} onClick={handleOnStart}>
                <CameraIcon />
                <span>Start video chat</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <BottomPanels
        panels={[
          {
            enabled: hasOwnChannel,
            name: BottomPanelKey.UPDATE_PLAN,
            show: bottomSheet.state.contentKey === BottomPanelKey.UPDATE_PLAN,
            title: 'Available Minutes Expired',
            component: <UpgradePanel />
          },
          {
            enabled: hasOwnChannel,
            name: BottomPanelKey.SETUP,
            show: bottomSheet.state.contentKey === BottomPanelKey.SETUP,
            title: 'Start event',
            component: (
              <SetupPanel
                type="scheduled"
                eventCode={code}
                eventName={name}
                isMembersOnly={memberOnly}
                isRecordingEnabled={!!recordingEnabled}
                onSubmitted={onStarted}
              />
            )
          },
          {
            enabled: hasOwnChannel,
            name: BottomPanelKey.EDIT_SCHEDULED_EVENT,
            show: bottomSheet.state.contentKey === BottomPanelKey.EDIT_SCHEDULED_EVENT,
            title: 'Edit event',
            component: <EditScheduledEventPanel event={event} onSubmitted={handleOnEdited} />
          },
          {
            name: BottomPanelKey.SHARE,
            show: bottomSheet.state.contentKey === BottomPanelKey.SHARE,
            title: isDesktop ? 'Share it via social media' : 'Share this event',
            component: <SharePanel event={event} />
          },
          {
            enabled: isOwnEvent,
            name: BottomPanelKey.EVENT_IS_OVERDUE,
            show: bottomSheet.state.contentKey === BottomPanelKey.EVENT_IS_OVERDUE,
            closeOnClickBackdrop: false,
            title: 'Your event is overdue',
            renderHeader: ({ title, headerClasses, titleClasses }) => (
              <div className={headerClasses}>
                <div className={titleClasses}>{title}</div>
              </div>
            ),
            component: <OverduePanel onDelete={handleOnDelete} />
          }
        ]}
        onClose={bottomSheet.close}
      />

      <ConfirmPanel
        show={confirmation.state.isOpen}
        name="DELETE_EVENT"
        title="Do you want to delete this event?"
        message="You cannot restore the event after deleting"
        submitText="Delete event"
        onCancel={handleOnDeletionCancelled}
        onSubmit={handleOnDeletionSubmitted}
      />
    </>
  );
};

export default UpcomingEventDetail;
